/**
 * Dados do emissor a imprimir no cabeçalho de um documento.
 *
 * Os componentes de PDF leem `clinica.endereco`, `clinica.contacto`, etc. Em vez
 * de mexer em cada um deles, damos-lhes um objeto com a MESMA forma da clínica
 * mas já com a morada e os contactos da unidade certa.
 *
 * O que muda entre unidades é o LOCAL. O NIF, o nome fiscal e o número de
 * certidão são da empresa e nunca são substituídos — há um NIF só.
 *
 * ─── POR GRUPO, NÃO POR CAMPO ───────────────────────────────────────────────
 *
 * A morada e os contactos são blocos. Se a unidade define algum contacto, usam-se
 * SÓ os contactos dela; se não define nenhum, usam-se os da clínica. Nunca uma
 * mistura.
 *
 * Antes o recurso era campo a campo, e isso misturava as duas: a Assomada tem
 * dois contactos, a Praia tem três — e o orçamento da Assomada saía com os dois
 * da Assomada mais o terceiro da Praia. Um número que toca noutra cidade, num
 * documento que o paciente leva para casa. A morada tinha o mesmo defeito à
 * espera de acontecer: rua da unidade com cidade da sede.
 *
 * O email e o logótipo continuam campo a campo. São um só valor cada, e o da
 * empresa é um recurso legítimo — não aponta para o sítio errado.
 *
 * @param {object} clinica         objeto da clínica (ClinicaContext)
 * @param {object} [estabelecimento] a unidade; sem ela devolve a clínica tal e qual
 */

const CAMPOS_MORADA = ["endereco", "cidade", "ilha", "codigoPostal"];
const CAMPOS_CONTACTO = ["contacto", "contacto2", "contacto3"];

/** Tem algum destes campos preenchido? Cadeias só com espaços não contam. */
const temAlgum = (objeto, campos) =>
  campos.some((c) => String(objeto?.[c] ?? "").trim() !== "");

/**
 * O bloco inteiro de quem o tiver: da unidade se ela definir algum campo dele,
 * da clínica caso contrário. Os campos que a unidade não tem ficam vazios — e
 * não emprestados.
 */
const blocoDe = (estabelecimento, clinica, campos) => {
  const origem = temAlgum(estabelecimento, campos) ? estabelecimento : clinica;
  return Object.fromEntries(campos.map((c) => [c, origem?.[c] || ""]));
};

export const dadosImpressao = (clinica, estabelecimento) => {
  if (!clinica) return clinica;
  if (!estabelecimento) return clinica;

  return {
    ...clinica,
    // Localização — um bloco só, de quem o tiver
    ...blocoDe(estabelecimento, clinica, CAMPOS_MORADA),
    ...blocoDe(estabelecimento, clinica, CAMPOS_CONTACTO),

    email: estabelecimento.email || clinica.email,
    logotipo: estabelecimento.logotipo || clinica.logotipo,

    // Cabeçalho/rodapé impressos: se a morada difere, a imagem normalmente
    // também. Só se substitui quando a unidade define os seus.
    cabecalhoRodape: {
      ...clinica.cabecalhoRodape,
      ...(estabelecimento.cabecalhoRodape?.cabecalho && {
        cabecalho: estabelecimento.cabecalhoRodape.cabecalho,
      }),
      ...(estabelecimento.cabecalhoRodape?.rodape && {
        rodape: estabelecimento.cabecalhoRodape.rodape,
      }),
    },

    // Para quem quiser mostrar o nome da unidade no documento.
    estabelecimentoNome: estabelecimento.nome,
    estabelecimentoCodigo: estabelecimento.codigo,
  };
};
